import { useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/icon';
import { Payout, useCorrectManualMutation, useRemoveManualMutation } from 'store/api/payout';
import { formatPrice } from 'utils/helpers';
import * as Styles from './subrows';
import { getPayoutItemSignedAmount } from './helpers';

const Rows = styled(Styles.SubRows)`
  button { border: 0; border-radius: 6px; cursor: pointer; }
  button:disabled { cursor: default; opacity: .45; }
  button:focus-visible { outline: 2px solid #245ac5; outline-offset: 2px; }
  button[title] { width: 28px; height: 28px; min-height: 28px; display: inline-flex; align-items: center; justify-content: center; }
  button svg { width: 18px; height: 18px; }
  label { display: grid; gap: 5px; font-size: 14px; }
  input, textarea { width: 100%; border: 1px solid #cbd1df; border-radius: 6px; padding: 9px 10px; font: inherit; color: inherit; background: #fff; }
  textarea { min-height: 64px; resize: vertical; }
  .edit-actions button { padding: 8px 16px; color: #174eaf; background: #eaf0fb; font: inherit; }
  .edit-actions button:first-child { color: white; background: #1555c7; }
`;

export function ManualPayoutRows({rows, editable, disabled, onBusy, onRefresh, onFailure}: {
  rows: Payout[]; editable: boolean; disabled: boolean; onBusy: (value: boolean) => void; onRefresh: () => Promise<void>; onFailure?: () => void;
}) {
  const [edit, setEdit] = useState<number>();
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [save] = useCorrectManualMutation();
  const [remove] = useRemoveManualMutation();
  const finish = () => {setEdit(undefined); onBusy(false);};
  const act = async (item: Payout, deleting = false) => {
    if (working || disabled) return;
    setWorking(true); onBusy(true); setError('');
    try {
      if (deleting) await remove(item.id).unwrap();
      else await save({id:item.id, amount:Number(amount), notes}).unwrap();
      await onRefresh(); finish();
    } catch (e: any) { onFailure?.(); setError(e?.data?.message || 'Unable to save this correction. The saved entry is still shown.'); }
    finally {setWorking(false); if (deleting) onBusy(false);}
  };
  return <Rows>
    {rows.map(item => <div key={item.id} style={{padding:0}}>
      <Styles.SubRow>
        <Styles.SubDescription>
        <span>{item.notes?.trim() ? item.notes : null}</span>
        <Styles.SubActions>
        {editable && item.canEditManual && <button type="button" title="Edit" aria-label={`Edit entry ${item.id}`} disabled={disabled || working || edit !== undefined} onClick={() => {setEdit(item.id);setAmount(String(item.amount));setNotes(item.notes || '');setError('');onBusy(true);}} style={{border:0,background:'transparent',padding:4,cursor:'pointer'}}><Icon.Edit /></button>}
        {editable && item.canRemoveManual && <button type="button" title="Remove" aria-label={`Remove entry ${item.id}`} disabled={disabled || working || edit !== undefined} onClick={() => void act(item,true)} style={{border:0,background:'transparent',padding:4,cursor:'pointer'}}><Icon.Delete /></button>}
        </Styles.SubActions>
        </Styles.SubDescription>
        <Styles.SubAmount $negative={getPayoutItemSignedAmount(item) < 0}>{getPayoutItemSignedAmount(item) >= 0 ? '+' : '−'}{formatPrice(Math.abs(getPayoutItemSignedAmount(item)))}</Styles.SubAmount>
      </Styles.SubRow>
      {edit === item.id && <div style={{display:'grid',gap:8,marginTop:8}}>
        <label>Amount<input aria-label="Entry amount" type="number" step="0.01" value={amount} disabled={working} onChange={e=>setAmount(e.target.value)} /></label>
        <label>Description<textarea aria-label="Entry description" value={notes} disabled={working} onChange={e=>setNotes(e.target.value)} /></label>
        <div className="edit-actions" style={{display:'flex',gap:8}}><button type="button" disabled={disabled || working || !Number.isFinite(Number(amount)) || Number(amount) === 0} onClick={()=>void act(item)}>Save</button><button type="button" disabled={working} onClick={finish}>Cancel</button></div>
      </div>}
    </div>)}
    {error && <p role="alert">{error}</p>}
  </Rows>;
}
